export const createOperationRequestTracker = () => {
  const trackers = new WeakMap();
  const requestMetadata = new WeakMap();

  const isState = localState =>
    (typeof localState === 'object' && localState !== null) ||
    typeof localState === 'function';

  const trackerFor = localState => {
    if (!isState(localState)) throw new TypeError('localState is required');

    let tracker = trackers.get(localState);
    if (!tracker) {
      tracker = {
        activeTokens: new Set(),
        latestByGroup: new Map(),
        tokenGroups: new Map(),
      };
      trackers.set(localState, tracker);
    }
    return tracker;
  };

  const identityKey = identity => {
    if (identity === undefined) return Symbol('independent-operation');
    if (typeof identity === 'number' && Number.isSafeInteger(identity)) {
      return `id:${identity}`;
    }
    if (typeof identity === 'string' && identity.trim()) {
      return `id:${identity}`;
    }

    throw new TypeError('operation identity must be a string or safe integer');
  };

  const begin = (localState, { group, identity } = {}) => {
    if (typeof group !== 'string' || !group.trim()) {
      throw new TypeError('operation group is required');
    }

    const normalizedGroup = group.trim();
    const tracker = trackerFor(localState);
    const token = Symbol(normalizedGroup);
    const key = identityKey(identity);
    let latestByKey = tracker.latestByGroup.get(normalizedGroup);
    if (!latestByKey) {
      latestByKey = new Map();
      tracker.latestByGroup.set(normalizedGroup, latestByKey);
    }
    latestByKey.set(key, token);
    tracker.activeTokens.add(token);
    tracker.tokenGroups.set(token, normalizedGroup);

    const request = Object.freeze({ group: normalizedGroup });
    requestMetadata.set(request, {
      finished: false,
      group: normalizedGroup,
      key,
      localState,
      token,
      tracker,
    });
    return request;
  };

  const isLatest = (localState, request) => {
    const metadata = requestMetadata.get(request);
    if (
      !metadata ||
      metadata.finished ||
      metadata.localState !== localState ||
      trackers.get(localState) !== metadata.tracker
    ) {
      return false;
    }

    return (
      metadata.tracker.activeTokens.has(metadata.token) &&
      metadata.tracker.latestByGroup.get(metadata.group)?.get(metadata.key) ===
        metadata.token
    );
  };

  const activeCount = (tracker, group) => {
    let count = 0;
    tracker.activeTokens.forEach(token => {
      if (tracker.tokenGroups.get(token) === group) count += 1;
    });
    return count;
  };

  const finish = (localState, request) => {
    const metadata = requestMetadata.get(request);
    if (!metadata || metadata.localState !== localState) {
      return { isCurrentTracker: false, activeCount: 0 };
    }

    if (metadata.finished) {
      return {
        isCurrentTracker: false,
        activeCount: activeCount(metadata.tracker, metadata.group),
      };
    }

    metadata.finished = true;
    metadata.tracker.activeTokens.delete(metadata.token);
    const latestByKey = metadata.tracker.latestByGroup.get(metadata.group);
    if (latestByKey?.get(metadata.key) === metadata.token) {
      latestByKey.delete(metadata.key);
      if (latestByKey.size === 0) {
        metadata.tracker.latestByGroup.delete(metadata.group);
      }
    }
    metadata.tracker.tokenGroups.delete(metadata.token);

    return {
      isCurrentTracker: trackers.get(localState) === metadata.tracker,
      activeCount: activeCount(metadata.tracker, metadata.group),
    };
  };

  const reset = localState =>
    isState(localState) && trackers.delete(localState);

  return { begin, isLatest, finish, reset };
};

export default createOperationRequestTracker;
